// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { MathUtils } from "../../Utils/MathUtils.js";
import { ErrorInformationStorage } from "../ErrorInformationStorage.js";
import { AccountType } from "../../Utils/Account/AccountType.js";
import { AccountAdditionalInfoItem } from "../../Utils/Account/AdditionalInfoItem.js";

export let Asset = function (dataCache, assetMessage)
{
    this.DataCache = dataCache;
    this.Id;                //int 
    this.Type;              //AssetType 
    this.Name;              //string 
    this.Description;       //string            
    this.MinChange;         //double 
    /// <summary>
    /// Рассчетное поле на основании мин чейнджа
    /// </summary>
    this.Point = 2;                  //int
    this.InstrumentId = -1;          //int
    this.RouteId = -1;               //int
    this.CreditForSell = null;       //double?
    this.LiquidityRate = null;       //double?
    this.InstrumentTradableID = -1;
    this._instrument = null;
    this._sellExchanges = null;

    this._insForTading = null;

    this.floatDecimalFormatStatic = null;

    this.FillByMessage(assetMessage);
    this.CreateFormatter();
}

Asset.prototype.toJSON = function ()
{
    return {
        Id: this.Id,
        Type: this.Type,
        Name: this.Name
    }
}

Asset.prototype.FillByMessage = function (message)
{
    this.Id = message.Id;
    this.Name = message.Name;
    this.Type = message.Type;
    this.Description = message.Description;
    this.MinChange = message.MinChange;
    if (this.MinChange > 0)
        this.Point = MathUtils.GetDecimalPlaces(this.MinChange, 8);
    if (message.InstrumentId !== null)
        this.InstrumentId = message.InstrumentId;
    if (message.RouteId !== null)
        this.RouteId = message.RouteId;

    this.CreditForSell = message.CreditForSell;
    this.LiquidityRate = message.LiquidityRate;
    this.InstrumentTradableID = message.InstrumentTradableID;
}

Asset.prototype.toString = function ()
{
    return this.Name;
}

Asset.prototype.formatPrice = function (value, noCurrency)
{
    if (isNaN(value))
        value = 0

    value = MathUtils.TruncateDouble(value, this.Point)    // для предотвращения округления в форматере

    if (noCurrency)
        return this.floatDecimalFormatStatic.format(value)

    return this.floatDecimalFormatStatic.format(value) + ' ' + this.Name;
}


Object.defineProperty(Asset.prototype, 'Instrument',
    {
        get: function ()  
        {
            if (this._instrument && !this.instrPr)
                return this._instrument;

            if (this.instrPr)
                return null
            this.instrPr = this.GetInstrumentAsync().then(function (instrument)
            {
                this._instrument = instrument;
                this.instrPr = null;
            }.bind(this));

            return null;
        }
    })

Asset.prototype.GetInstrumentAsync = function ()
{
    if (this.InstrumentTradableID !== -1 && this.RouteId !== -1)
    {
        return this.DataCache.getInstrumentByInstrumentTradableID_NFL(this.InstrumentTradableID, this.RouteId).then(function (instrument)
        {
            if (!instrument)
                return null;

            if (instrument.TradableRoutes.indexOf(instrument.Route) === -1 && instrument.TradableRoutes.length)
                return this.DataCache.getInstrumentByInstrumentTradableID_NFL(this.InstrumentTradableID, instrument.TradableRoutes[0]);

            return instrument;
        }.bind(this))
        // var list = BaseApplication.App.MultiDataCache.Instruments.GetNonFixedInstrumentsByTradableID(TradableId);
        // if (list.Count(x => x.RouteId == this.RouteId) != 0 && list.Count == 2) // кейс, когда схлопываются роуты
        //     return list.Where(x => x.RouteId != this.RouteId).FirstOrDefault();
    }

    return Promise.resolve(null);
}

Object.defineProperty(Asset.prototype, 'SellExchanges',
    {
        get: function ()  
        {
            if (this._sellExchanges)
                return this._sellExchanges;

            this.UpdateSellExchanges();

            return null;
        }
    })

Asset.prototype.GetPreferedInstrumentInterriorIdByExchange = function (exchange)
{
    if (!this._insForTading)
        return null;
    let arr = this._insForTading[exchange]
    if (!arr)
        return null;
    return arr[0] || null;
}

Asset.prototype.UpdateSellExchanges = function ()
{
    return this.DataCache.GetNonFixedInstrumentListByAssetName(this.Name)
        .then(function (instruments)
        {
            let sellExchangesObj = new Object()
            let insForTading = new Object();
            for (let i = 0; i < instruments.length; i++)
            {
                let ins = instruments[i];
                sellExchangesObj[ins.TradingExchange] = true;
                if (!insForTading[ins.TradingExchange])
                    insForTading[ins.TradingExchange] = new Array();
                insForTading[ins.TradingExchange].push(ins.GetInteriorID())
            }
            this._insForTading = insForTading;
            let sellExchangesArray = Object.keys(sellExchangesObj);
            sellExchangesArray.sort();

            if (!this._instrument)
                return this.GetInstrumentAsync()
                    .then(function (instrument)
                    {
                        this._instrument = instrument
                        return sellExchangesArray;
                    }.bind(this))

            return sellExchangesArray;
        }.bind(this))
        .then(function (sellExchangesArray)
        {
            if (!this._instrument)
                return sellExchangesArray

            let index = sellExchangesArray.indexOf(this._instrument.TradingExchange)

            sellExchangesArray.splice(index, 1);
            sellExchangesArray.unshift(this._instrument.TradingExchange);

            this._sellExchanges = sellExchangesArray;
            return this._sellExchanges;
        }.bind(this))
}

Asset.prototype.CreateFormatter = function ()
{
    //nicky
    //при форматировании цены применялись точности из ассета (Point)
    //и при параллельных запросах очень часто возникал баз из-за
    //использования несинхронизированного доступа к форматтеру из инструмента.
    //
    //чтобы не ухудшать перфоманс локом, вынес каждому ассету личный форматтер
    //
    this.floatDecimalFormatStatic = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: this.Point,
        maximumFractionDigits: this.Point
    })
}

Asset.IsEqualAsset = function (a1, a2)
{
    if (!a1 || !a2)
        return false
    return a1.Id === a2.Id
}

export let AssetType = { CURRENCY: 0, SHARES: 1, COMMODITIES: 2, CRYPTO_CCY: 3 };
AssetType.getStr = function (value)
{
    switch (value)
    {
        case 0:
            return "CURRENCY"
        case 1:
            return "SHARES"
        case 2:
            return "COMMODITIES"
        case 3:
            return "CRYPTO_CCY"
        default:
            return "CURRENCY"
    }
}

// #region AssetBalance

// TODO. Inherit from DataCacheBusinessObject.
export let AssetBalance = function (account, isEmpty)
{
    this.account = account

    this.FDataCache = account.DataCache

    isEmpty = isEmpty || false

    this.IsEmpty = isEmpty
    this.assetBalanceData = isEmpty
        ? AssetBalanceData.Empty
        : new AssetBalanceData()

    this.Asset = null
}

AssetBalance.prototype.RecreateData = function ()
{
    this.assetBalanceData = new AssetBalanceData()
    this.IsEmpty = false
}

AssetBalance.prototype.FillByMessage = function (mess)
{
    let assetBalanceData = this.assetBalanceData
    if (mess.Balance !== null)
        assetBalanceData.balance = mess.Balance
    if (mess.BlockedSum !== null)
        assetBalanceData.blockedSum = mess.BlockedSum
    if (mess.CashBalance !== null)
        assetBalanceData.cashBalance = mess.CashBalance
    if (mess.BeginBalance !== null)
        assetBalanceData.beginBalance = mess.BeginBalance
    if (mess.RealizedPnl !== null)
        assetBalanceData.realizedPnl = mess.RealizedPnl
    // nicky fix (по согласованию с сашей и женей)
    // сервером присылается + если с меня снимают деньги, + если приплюсовывают.
    if (mess.TodayFees !== null)
        assetBalanceData.todayFees = -mess.TodayFees
    if (mess.TodayTurnover !== null)
        assetBalanceData.todayTurnover = mess.TodayTurnover
    if (mess.BlockedForOrders !== null)
        assetBalanceData.blockedForOrders = mess.BlockedForOrders
    // if (mess.LockedForPAMM !== null)
    //     assetBalanceData.blockedForPAMM = mess.LockedForPAMM
    if (mess.ReservedBalance !== null)
        assetBalanceData.reservedBalance = mess.ReservedBalance
    if (mess.CreditValue !== null)
        assetBalanceData.creditValue = mess.CreditValue
    if (mess.UsedMargin !== null)
        assetBalanceData.minMargin = mess.UsedMargin
    if (mess.AvailableMargin !== null)
        assetBalanceData.availableMargin = mess.AvailableMargin
    if (mess.MaintanceMargin !== null)
        assetBalanceData.maintanceMargin = mess.MaintanceMargin
    if (mess.MarginDeficiency !== null)
        assetBalanceData.marginDeficiency = mess.MarginDeficiency
    if (mess.MarginSurplus !== null)
        assetBalanceData.marginSurplus = mess.MarginSurplus
    if (mess.InitialMarginWithoutWaived !== null)
        assetBalanceData.InitialMarginWithoutWaived = mess.InitialMarginWithoutWaived
    if (mess.TodayTrades !== null)
        assetBalanceData.todayTrades = mess.TodayTrades
    if (mess.TodayVolume !== null)
        assetBalanceData.todayVolume = mess.TodayVolume
    if (mess.TodayRebates !== null)
        assetBalanceData.todayRebates = mess.TodayRebates
    if (mess.TodaySwaps !== null)
        assetBalanceData.todaySwaps = mess.TodaySwaps
    if (mess.UnsettledLoss !== null)
        assetBalanceData.unsettledLoss = mess.UnsettledLoss
    if (mess.UnsettledProfit !== null)
        assetBalanceData.unsettledProfit = mess.UnsettledProfit
    if (mess.CurrentDailyLoss !== null)
        assetBalanceData.currentDailyLoss = mess.CurrentDailyLoss
    if (mess.CurrentWeeklyLoss !== null)
        assetBalanceData.currentWeeklyLoss = mess.CurrentWeeklyLoss
    if (mess.TrailingDrawdownLevel !== null)
        assetBalanceData.trailingDrawdownLevel = mess.TrailingDrawdownLevel
    if (mess.StockOrdersReq !== null)
        assetBalanceData.stockOrdersReq = mess.StockOrdersReq
    if (mess.UsedUnsettledNegativePremium !== null)
        assetBalanceData.UsedUnsettledNegativePremium = mess.UsedUnsettledNegativePremium
    if (mess.UnusedUnsettledNegativePremium !== null)
        assetBalanceData.UnusedUnsettledNegativePremium = mess.UnusedUnsettledNegativePremium
    if (mess.UnsettledPositivePremium !== null)
        assetBalanceData.UnsettledPositivePremium = mess.UnsettledPositivePremium
    if (mess.UnsettledPremiumFromOpenSell !== null)
        assetBalanceData.UnsettledPremiumFromOpenSell = mess.UnsettledPremiumFromOpenSell
    if (mess.UsedPremiumFromOpenSell !== null)
        assetBalanceData.UsedPremiumFromOpenSell = mess.UsedPremiumFromOpenSell
    if (mess.UnusedPremiumFromOpenSell !== null)
        assetBalanceData.UnusedPremiumFromOpenSell = mess.UnusedPremiumFromOpenSell
    if (mess.UsedUnsettledNegativeCashForStocks !== null)
        assetBalanceData.UsedUnsettledNegativeCashForStocks = mess.UsedUnsettledNegativeCashForStocks
    if (mess.UnusedUnsettledNegativeCashForStocks !== null)
        assetBalanceData.UnusedUnsettledNegativeCashForStocks = mess.UnusedUnsettledNegativeCashForStocks
    if (mess.UnsettledPositiveCashForStocks !== null)
        assetBalanceData.UnsettledPositiveCashForStocks = mess.UnsettledPositiveCashForStocks
    if (mess.StocksLiquidity !== null)
        assetBalanceData.StocksLiquidity = mess.StocksLiquidity
    if (mess.OptionPremiumReq !== null)
        assetBalanceData.OptionPremiumReq = mess.OptionPremiumReq
    if (mess.UnsettledLossAvailableForWithdrawal !== null)
        assetBalanceData.UnsettledLossAvailableForWithdrawal = mess.UnsettledLossAvailableForWithdrawal
    if (mess.WithdrawableCashForStocks !== null)
        assetBalanceData.WithdrawableCashForStocks = mess.WithdrawableCashForStocks;

    if (mess.WithdrawableUnsettledPnL !== null)
        assetBalanceData.WithdrawableUnsettledPnL = mess.WithdrawableUnsettledPnL;


    if (mess.UnsettledCollateral !== null)
        assetBalanceData.UnsettledCollateral = mess.UnsettledCollateral;

    if (mess.WithdrawableUnsettledCollateral !== null)
        assetBalanceData.WithdrawableUnsettledCollateral = mess.WithdrawableUnsettledCollateral;

    if (mess.Collateral !== null)
        assetBalanceData.Collateral = mess.Collateral;

    if (mess.UnusedSettledCashForStocks !== null)
        assetBalanceData.UnusedSettledCashForStocks = mess.UnusedSettledCashForStocks;
    if (mess.UnusedSettledCollateral !== null)
        assetBalanceData.UnusedSettledCollateral = mess.UnusedSettledCollateral;

    if (mess.UnsettledDeposit !== null)
        assetBalanceData.unsettledDeposit = mess.UnsettledDeposit;

    if (mess.UnusedIntradayInitialMargin !== null)
        assetBalanceData.UnusedIntradayInitialMargin = mess.UnusedIntradayInitialMargin;
    if (mess.UsedOvernightInitialMarginForIntraday !== null)
        assetBalanceData.UsedOvernightInitialMarginForIntraday = mess.UsedOvernightInitialMarginForIntraday;

    if (mess.AccruedDividends !== null)
        assetBalanceData.AccruedDividends = mess.AccruedDividends;

    if (mess.WithdrawableOptionsPremium !== null)
        assetBalanceData.WithdrawableOptionsPremium = mess.WithdrawableOptionsPremium;


    if (mess.WarningMarginRequirement !== null)
        assetBalanceData.warningMarginRequirement = mess.WarningMarginRequirement;

    if (mess.MarginBeforeWarning !== null)
        assetBalanceData.marginBeforeWarning = mess.MarginBeforeWarning;

    if (mess.WithdrawalAvailable !== null)
        assetBalanceData.WithdrawalAvailable = mess.WithdrawalAvailable;

    let AssetAdditionalInfoUpdated = false;
    if (mess.AssetAdditionalInfo != null)
    {
        for (let itemKey in mess.AssetAdditionalInfo)
        {
            if (!mess.AssetAdditionalInfo.hasOwnProperty(itemKey))
                continue;

            let itemValue = mess.AssetAdditionalInfo[itemKey];

            if (assetBalanceData.AssetAdditionalInfo[itemKey])
                assetBalanceData.AssetAdditionalInfo[itemKey].Update(itemValue);
            else
            {
                var newItem = new AccountAdditionalInfoItem();
                newItem.Update(itemValue);
                assetBalanceData.AssetAdditionalInfo[itemKey] = newItem;
                AssetAdditionalInfoUpdated = true;
            }
        }
    }

    if (mess.BuyingPowerAvailableFunds !== null)
        assetBalanceData.BuyingPowerAvailableFunds = mess.BuyingPowerAvailableFunds     // #106952

    return AssetAdditionalInfoUpdated;
}

Object.defineProperty(AssetBalance.prototype, 'TodayRealizedPNL',
    {
        get: function ()
        {
            let assetBalanceData = this.assetBalanceData
            return isNaN(assetBalanceData.realizedPnl)
                ? assetBalanceData.balance - assetBalanceData.beginBalance
                : assetBalanceData.realizedPnl
        }
    })
Object.defineProperty(AssetBalance.prototype, 'TodayRealizedPNLCrossPrice',
    {
        get: function ()
        {
            return this.TodayRealizedPNL * this.getCrossPrice()
        }
    })
Object.defineProperty(AssetBalance.prototype, 'TodayFees',
    {
        get: function () { return this.assetBalanceData.todayFees; },
    })
Object.defineProperty(AssetBalance.prototype, 'TodayFeesCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.todayFees * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'TodayGrossPNL',
    {
        get: function () { return this.assetBalanceData.realizedPnl - this.assetBalanceData.todayFees - this.assetBalanceData.todaySwaps; },
    })
Object.defineProperty(AssetBalance.prototype, 'TodayGrossPNLCrossPrice',
    {
        get: function ()
        {
            return this.TodayGrossPNL * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'TodayVolume',
    {
        get: function () { return this.assetBalanceData.todayVolume; }
        //set: function (value) { assetBalanceData.todayVolume = value; },
    })
Object.defineProperty(AssetBalance.prototype, 'TodayTrades',
    {
        get: function () { return this.assetBalanceData.todayTrades; }
        //set: function (value) { assetBalanceData.todayTrades = value; },
    })
Object.defineProperty(AssetBalance.prototype, 'TodayRebates',
    {
        get: function () { return this.assetBalanceData.todayRebates; }
        //set: function (value) { assetBalanceData.todayRebates = value; },
    })
Object.defineProperty(AssetBalance.prototype, 'TodayRebatesCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.todayRebates * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'WithdrawForAccount',
    {
        get: function ()
        {
            if (this.assetBalanceData.blockedSum < Number.MAX_VALUE && this.assetBalanceData.blockedSum > Number.MIN_VALUE)
                return this.assetBalanceData.blockedSum;
            else
                return 0;
        }
    })
Object.defineProperty(AssetBalance.prototype, 'BalanceCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.balance * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'BlockedSumCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.blockedSum * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'MinMarginCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.minMargin * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'MarginAvailableCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.availableMargin * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'MaintMarginForPositionCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.maintanceMargin * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'BlockedForOrdersCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.blockedForOrders * this.getCrossPrice();
        }
    })
// Object.defineProperty(AssetBalance.prototype, 'BlockedForPammCrossPrice',
//     {
//         get: function ()
//         {
//             return this.assetBalanceData.blockedForPAMM * this.getCrossPrice();
//         }
//     })

Object.defineProperty(AssetBalance.prototype, 'CreditValueCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.creditValue * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'MarginDeficiencyCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.marginDeficiency * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'MarginSurplusCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.marginSurplus * this.getCrossPrice();
        }
    })
Object.defineProperty(AssetBalance.prototype, 'TodaySwapsCrossPrice',
    {
        get: function ()
        {
            return this.assetBalanceData.todaySwaps * this.getCrossPrice();
        }
    })

Object.defineProperty(AssetBalance.prototype, 'AssetAdditionalInfo',
    {
        get: function ()
        {
            return this.assetBalanceData.AssetAdditionalInfo;
        }
    })
//Object.defineProperty(AssetBalance.prototype, 'MaintMarginForPositionCrossPrice',
//{
//    get: function ()
//    {
//        return this.assetBalanceData.maintanceMargin * this.getCrossPrice();
//    }
//});

//#region Server data
Object.defineProperty(AssetBalance.prototype, 'AvailableMargin',
    {
        get: function () { return this.assetBalanceData.availableMargin; },
        set: function (value) { this.assetBalanceData.availableMargin = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'StocksLiquidity',
    {
        get: function () { return this.assetBalanceData.StocksLiquidity; },
        set: function (value) { this.assetBalanceData.StocksLiquidity = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'BalancePlusAllRisks',
    {
        get: function () { return this.Balance /*+ StockValueDiscount + ProfitForMargin*/; },
    });
Object.defineProperty(AssetBalance.prototype, 'Balance',
    {
        get: function () { return this.assetBalanceData.balance; },
        set: function (value) { this.assetBalanceData.balance = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'BeginBalance',
    {
        get: function () { return this.assetBalanceData.beginBalance; },
        set: function (value) { this.assetBalanceData.beginBalance = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'BlockedForOrders',
    {
        get: function () { return this.assetBalanceData.blockedForOrders; },
        set: function (value) { this.assetBalanceData.blockedForOrders = value; },
    });
// Object.defineProperty(AssetBalance.prototype, 'BlockedForPAMM',
//     {
//         get: function () { return this.assetBalanceData.blockedForPAMM; },
//         set: function (value) { this.assetBalanceData.blockedForPAMM = value; },
//     });
Object.defineProperty(AssetBalance.prototype, 'BlockedSum',
    {
        get: function () { return this.assetBalanceData.blockedSum; },
        set: function (value) { this.assetBalanceData.blockedSum = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'CashBalance',
    {
        get: function () { return this.assetBalanceData.cashBalance; },
        set: function (value) { this.assetBalanceData.cashBalance = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'CreditValue',
    {
        get: function () { return this.assetBalanceData.creditValue; },
        set: function (value) { this.assetBalanceData.creditValue = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'RealizedPnl',
    {
        get: function () { return this.assetBalanceData.realizedPnl; },
        set: function (value) { this.assetBalanceData.realizedPnl = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'MaintanceMargin',
    {
        get: function () { return this.assetBalanceData.maintanceMargin; },
        set: function (value) { this.assetBalanceData.maintanceMargin = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'MarginDeficiency',
    {
        get: function () { return this.assetBalanceData.marginDeficiency; },
        set: function (value) { this.assetBalanceData.marginDeficiency = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'MarginSurplus',
    {
        get: function () { return this.assetBalanceData.marginSurplus; },
        set: function (value) { this.assetBalanceData.marginSurplus = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'ReservedBalance',
    {
        get: function () { return this.assetBalanceData.reservedBalance; },
        set: function (value) { this.assetBalanceData.reservedBalance = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'TodayTurnover',
    {
        get: function () { return this.assetBalanceData.todayTurnover; },
        set: function (value) { this.assetBalanceData.todayTurnover = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'MinMargin',
    {
        get: function () { return this.assetBalanceData.minMargin; },
        set: function (value) { this.assetBalanceData.minMargin = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'TodaySwaps',
    {
        get: function () { return this.assetBalanceData.todaySwaps; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnsettledProfit',
    {
        get: function () { return this.assetBalanceData.unsettledProfit; },
        set: function (value) { this.assetBalanceData.unsettledProfit = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnsettledLoss',
    {
        get: function () { return this.assetBalanceData.unsettledLoss; },
        set: function (value) { this.assetBalanceData.unsettledLoss = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnsettledLossAvailableForWithdrawal',
    {
        get: function () { return this.assetBalanceData.UnsettledLossAvailableForWithdrawal; },
        set: function (value) { this.assetBalanceData.UnsettledLossAvailableForWithdrawal = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnusedSettledCashForStocks',
    {
        get: function () { return this.assetBalanceData.UnusedSettledCashForStocks; },
        set: function (value) { this.assetBalanceData.UnusedSettledCashForStocks = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnsettledDeposit',
    {
        get: function () { return this.assetBalanceData.unsettledDeposit; },
        set: function (value) { this.assetBalanceData.unsettledDeposit = value; },
    });

Object.defineProperty(AssetBalance.prototype, 'UnusedSettledCollateral',
    {
        get: function () { return this.assetBalanceData.UnusedSettledCollateral; },
        set: function (value) { this.assetBalanceData.UnusedSettledCollateral = value; },
    });
/// <summary>
/// #98625
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'UnusedIntradayInitialMargin',
    {
        get: function () { return this.assetBalanceData.UnusedIntradayInitialMargin; },
        set: function (value) { this.assetBalanceData.UnusedIntradayInitialMargin = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UsedOvernightInitialMarginForIntraday',
    {
        get: function () { return this.assetBalanceData.UsedOvernightInitialMarginForIntraday; },
        set: function (value) { this.assetBalanceData.UsedOvernightInitialMarginForIntraday = value; },
    });

Object.defineProperty(AssetBalance.prototype, 'AccruedDividends',
    {
        get: function () { return this.assetBalanceData.AccruedDividends; },
        set: function (value) { this.assetBalanceData.AccruedDividends = value; }
    })
/// <summary>
/// #75187
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'WithdrawableCashForStocks',
    {
        get: function () { return this.assetBalanceData.WithdrawableCashForStocks; },
        set: function (value) { this.assetBalanceData.WithdrawableCashForStocks = value; },
    });
/// <summary>
/// #75187
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'WithdrawableUnsettledPnL',
    {
        get: function () { return this.assetBalanceData.WithdrawableUnsettledPnL; },
        set: function (value) { this.assetBalanceData.WithdrawableUnsettledPnL = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'TrailingDrawdownLevel',
    {
        get: function () { return this.assetBalanceData.trailingDrawdownLevel; },
        set: function (value) { this.assetBalanceData.trailingDrawdownLevel = value; },
    });
/// <summary>
/// #59262
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'StockOrdersReq',
    {
        get: function () { return this.assetBalanceData.stockOrdersReq; },
        set: function (value) { this.assetBalanceData.stockOrdersReq = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'CurrentDailyLoss',
    {
        get: function () { return this.assetBalanceData.currentDailyLoss; },
        set: function (value) { this.assetBalanceData.currentDailyLoss = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'CurrentWeeklyLoss',
    {
        get: function () { return this.assetBalanceData.currentWeeklyLoss; },
        set: function (value) { this.assetBalanceData.currentWeeklyLoss = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UsedUnsettledNegativePremium',
    {
        get: function () { return this.assetBalanceData.UsedUnsettledNegativePremium; },
        set: function (value) { this.assetBalanceData.UsedUnsettledNegativePremium = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnusedUnsettledNegativePremium',
    {
        get: function () { return this.assetBalanceData.UnusedUnsettledNegativePremium; },
        set: function (value) { this.assetBalanceData.UnusedUnsettledNegativePremium = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnsettledPositivePremium',
    {
        get: function () { return this.assetBalanceData.UnsettledPositivePremium; },
        set: function (value) { this.assetBalanceData.UnsettledPositivePremium = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnsettledPremiumFromOpenSell',
    {
        get: function () { return this.assetBalanceData.UnsettledPremiumFromOpenSell; },
        set: function (value) { this.assetBalanceData.UnsettledPremiumFromOpenSell = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UsedPremiumFromOpenSell',
    {
        get: function () { return this.assetBalanceData.UsedPremiumFromOpenSell; },
        set: function (value) { this.assetBalanceData.UsedPremiumFromOpenSell = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnusedPremiumFromOpenSell',
    {
        get: function () { return this.assetBalanceData.UnusedPremiumFromOpenSell; },
        set: function (value) { this.assetBalanceData.UnusedPremiumFromOpenSell = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UsedUnsettledNegativeCashForStocks',
    {
        get: function () { return this.assetBalanceData.UsedUnsettledNegativeCashForStocks; },
        set: function (value) { this.assetBalanceData.UsedUnsettledNegativeCashForStocks = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnusedUnsettledNegativeCashForStocks',
    {
        get: function () { return this.assetBalanceData.UnusedUnsettledNegativeCashForStocks; },
        set: function (value) { this.assetBalanceData.UnusedUnsettledNegativeCashForStocks = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'UnsettledPositiveCashForStocks',
    {
        get: function () { return this.assetBalanceData.UnsettledPositiveCashForStocks; },
        set: function (value) { this.assetBalanceData.UnsettledPositiveCashForStocks = value; },
    });
Object.defineProperty(AssetBalance.prototype, 'OptionPremiumReq',
    {
        get: function () { return this.assetBalanceData.OptionPremiumReq; },
        set: function (value) { this.assetBalanceData.OptionPremiumReq = value; },
    });

/// <summary>
/// #82716
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'UnsettledCollateral',
    {
        get: function () { return this.assetBalanceData.UnsettledCollateral; },
        set: function (value) { this.assetBalanceData.UnsettledCollateral = value; }
    });

/// <summary>
/// #82716
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'WithdrawableUnsettledCollateral',
    {
        get: function () { return this.assetBalanceData.WithdrawableUnsettledCollateral; },
        set: function (value) { this.assetBalanceData.WithdrawableUnsettledCollateral = value; }
    });

/// <summary>
/// #82716
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'Collateral',
    {
        get: function () { return this.assetBalanceData.Collateral; },
        set: function (value) { this.assetBalanceData.Collateral = value; }
    });

/// <summary>
/// #91468
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'WarningMarginRequirement',
    {
        get: function () { return this.assetBalanceData.warningMarginRequirement; },
    });
Object.defineProperty(AssetBalance.prototype, 'MarginBeforeWarning',
    {
        get: function () { return this.assetBalanceData.marginBeforeWarning; },
    });
/// <summary>
/// #110792
/// </summary>
Object.defineProperty(AssetBalance.prototype, 'WithdrawalAvailable',
    {
        get: function () { return this.assetBalanceData.WithdrawalAvailable; },
        set: function (value) { this.assetBalanceData.WithdrawalAvailable = value; }
    })

//#endregion

Object.defineProperty(AssetBalance.prototype, 'WithdrawableOptionsPremium',
    {
        get: function () { return this.assetBalanceData.WithdrawableOptionsPremium; },
        set: function (value) { this.assetBalanceData.WithdrawableOptionsPremium = value; }
    })

Object.defineProperty(AssetBalance.prototype, 'BuyingPowerAvailableFunds',
    {
        get: function () { return this.assetBalanceData.BuyingPowerAvailableFunds; },
        set: function (value) { this.assetBalanceData.BuyingPowerAvailableFunds = value; }
    })

AssetBalance.prototype.getCrossPrice = function ()
{
    return this.Asset ? this.FDataCache.CrossRateCache.GetCrossPriceExp1(this.Asset.Name) : 1
}

AssetBalance.prototype.formatPriceExactly = function (value, noCurrency = false)
{
    return this.Asset.formatPrice(value, noCurrency)
}
AssetBalance.prototype.formatPrice = function (value, noCurrency = false)
{
    return this.Asset.formatPrice(value, noCurrency);
}

AssetBalance.prototype.WithdrawalAvailableTruncateToAsset = function (val)
{
    return MathUtils.TruncateDouble(Math.max(val, 0), this.Asset.Point);
}

AssetBalance.prototype.WithdrawalAvaliable = function (acc)
{
    const valueFromServer = this.WithdrawalAvailable;    // #110792
    return valueFromServer !== null ?
        this.WithdrawalAvailableTruncateToAsset(valueFromServer) :
        this.AvaliableWithdrawCalculate(acc);
}

AssetBalance.prototype.AvaliableWithdrawCalculate = function (acc)
{
    try
    {
        let stockValueDiscount = 0;

        if (acc != null && acc.AccountType !== AccountType.MultiAsset)
            stockValueDiscount = acc.StockValueDiscount;

        //++64321
        let creditValue = this.CreditValue > 0 ? this.CreditValue : 0;

        let withdrawalAvailable = this.AvailableMargin
            - stockValueDiscount
            - creditValue
            - acc.OnlyProfitForWithdrawalAvailable
            - this.UnsettledProfit
            - this.UnsettledLossAvailableForWithdrawal
            + this.UnusedUnsettledNegativeCashForStocks
            - this.UnsettledPositiveCashForStocks
            + this.UnusedUnsettledNegativePremium
            - this.UnsettledPositivePremium
            + this.UnsettledPremiumFromOpenSell
            - this.UsedPremiumFromOpenSell
            + this.WithdrawableCashForStocks
            + this.WithdrawableUnsettledPnL
            + this.WithdrawableUnsettledCollateral
            - this.StocksLiquidity
            + this.UnusedIntradayInitialMargin
            - this.UsedOvernightInitialMarginForIntraday
            - this.AccruedDividends
            + this.WithdrawableOptionsPremium
            - this.UnsettledDeposit;

        return this.WithdrawalAvailableTruncateToAsset(withdrawalAvailable);
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        return 0;
    }
}
// #endregion AssetBalance

// #region AssetBalanceData

export let AssetBalanceData = function ()
{
    this.availableMargin = 0
    this.balance = 0
    this.beginBalance = 0
    this.blockedForOrders = 0
    // this.blockedForPAMM = 0
    this.blockedSum = 0
    this.cashBalance = 0
    this.creditValue = 0
    this.realizedPnl = 0
    this.maintanceMargin = 0
    this.warningMarginRequirement = 0
    this.marginBeforeWarning = 0
    this.marginDeficiency = 0
    this.marginSurplus = 0
    this.reservedBalance = 0
    this.todayFees = 0
    this.todayTurnover = 0
    this.minMargin = 0
    this.todayTrades = 0
    this.todayVolume = 0
    this.todayRebates = 0
    this.todaySwaps = 0
    this.unsettledProfit = 0
    this.unsettledLoss = 0
    this.unsettledDeposit = 0
    this.UnsettledLossAvailableForWithdrawal = 0
    this.currentDailyLoss = 0
    this.currentWeeklyLoss = 0
    this.trailingDrawdownLevel = -1
    this.stockOrdersReq = 0
    this.UsedUnsettledNegativePremium = 0
    this.UnusedUnsettledNegativePremium = 0
    this.UnsettledPositivePremium = 0
    this.UnsettledPremiumFromOpenSell = 0
    this.UsedPremiumFromOpenSell = 0
    this.UnusedPremiumFromOpenSell = 0
    this.UsedUnsettledNegativeCashForStocks = 0
    this.UnusedUnsettledNegativeCashForStocks = 0
    this.UnsettledPositiveCashForStocks = 0
    this.StocksLiquidity = 0
    this.OptionPremiumReq = 0
    this.WithdrawableCashForStocks = 0;
    this.WithdrawableUnsettledPnL = 0;

    //82717
    this.UnsettledCollateral = 0;
    this.WithdrawableUnsettledCollateral = 0;
    this.Collateral = 0;
    this.InitialMarginWithoutWaived = 0;
    //98161
    this.UnusedSettledCashForStocks = 0;
    this.UnusedSettledCollateral = 0;
    this.UnsettledDeposit = 0;

    //98625
    this.UnusedIntradayInitialMargin = 0;
    this.UsedOvernightInitialMarginForIntraday = 0;

    this.AccruedDividends = 0;
    this.AssetAdditionalInfo = new Object();

    this.WithdrawableOptionsPremium = 0;

    this.WithdrawalAvailable = null;

    this.BuyingPowerAvailableFunds = null   // array of BuyingPowerAvailableFund
}

AssetBalanceData.Empty = new AssetBalanceData()

// #endregion AssetBalanceData